import React, { useState, useEffect } from 'react';
import { useWorkoutsContext } from '../hooks/useWorkoutsContext';

const WorkoutForm = ({ editWorkout, onCancelEdit, onShowToast }) => {
  const { dispatch } = useWorkoutsContext();

  const [title, setTitle] = useState('');
  const [load, setLoad] = useState('');
  const [reps, setReps] = useState('');
  const [category, setCategory] = useState('Strength');
  const [notes, setNotes] = useState('');
  const [date, setDate] = useState(new Date().toISOString().split('T')[0]);
  const [weightUnit, setWeightUnit] = useState('lbs');

  const [error, setError] = useState(null);
  const [emptyFields, setEmptyFields] = useState([]);
  const [suggestions, setSuggestions] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Populate form if editing
  useEffect(() => {
    if (editWorkout) {
      setTitle(editWorkout.title || '');
      setLoad(editWorkout.load || '');
      setReps(editWorkout.reps || '');
      setCategory(editWorkout.category || 'Strength');
      setNotes(editWorkout.notes || '');
      setWeightUnit(editWorkout.weightUnit || 'lbs');

      // Date parsing for input date format (YYYY-MM-DD)
      try {
        const d = new Date(editWorkout.date || editWorkout.createdAt);
        if (!isNaN(d.getTime())) {
          setDate(d.toISOString().split('T')[0]);
        } else {
          setDate(new Date().toISOString().split('T')[0]);
        }
      } catch (e) {
        setDate(new Date().toISOString().split('T')[0]);
      }

      setError(null);
      setEmptyFields([]);
    } else {
      // Default reset
      setTitle('');
      setLoad('');
      setReps('');
      setCategory('Strength');
      setNotes('');
      setDate(new Date().toISOString().split('T')[0]);
      setWeightUnit('lbs');
      setError(null);
      setEmptyFields([]);
    }
  }, [editWorkout]);

  // Handle exercise suggestions fetch
  const handleSearch = async (searchTerm) => {
    if (searchTerm.length < 3) {
      setSuggestions([]);
      return;
    }

    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/workouts/exercises?name=${searchTerm}`);
      const json = await response.json();

      if (response.ok) {
        setSuggestions(json.slice(0, 5)); // Limit to top 5 suggestions
      }
    } catch (err) {
      console.error('Suggestions search failed:', err);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (isSubmitting) return;

    // Validate fields
    const missing = [];
    if (!title.trim()) missing.push('title');
    if (!load) missing.push('load');
    if (!reps) missing.push('reps');

    if (missing.length > 0) {
      setEmptyFields(missing);
      setError('Please fill in all required fields (Exercise Title, Load, Reps)');
      return;
    }

    setIsSubmitting(true);
    setError(null);

    const workoutData = {
      title,
      load: Number(load),
      reps: Number(reps),
      category,
      notes,
      date: new Date(date),
      weightUnit
    };

    try {
      const url = editWorkout
        ? `${process.env.REACT_APP_API_URL}/api/workouts/${editWorkout._id}`
        : `${process.env.REACT_APP_API_URL}/api/workouts`;

      const method = editWorkout ? 'PATCH' : 'POST';

      const response = await fetch(url, {
        method,
        body: JSON.stringify(workoutData),
        headers: {
          'Content-Type': 'application/json'
        }
      });
      const json = await response.json();

      if (!response.ok) {
        setError(json.error || 'Failed to save workout');
        if (json.emptyFields) setEmptyFields(json.emptyFields);
      } else {
        // Clear fields
        setTitle('');
        setLoad('');
        setReps('');
        setCategory('Strength');
        setNotes('');
        setDate(new Date().toISOString().split('T')[0]);
        setWeightUnit('lbs');
        setError(null);
        setEmptyFields([]);
        setSuggestions([]);

        if (editWorkout) {
          dispatch({ type: 'UPDATE_WORKOUT', payload: json });
          onShowToast(`Updated "${json.title}" successfully`, 'success');
          onCancelEdit(); // switch out of edit mode
        } else {
          dispatch({ type: 'CREATE_WORKOUT', payload: json });
          onShowToast(`Added "${json.title}" successfully`, 'success');
        }
      }
    } catch (err) {
      setError('Could not connect to the server. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <form className="create" onSubmit={handleSubmit}>
      <h3>
        {editWorkout ? (
          <>
            Edit Exercise
            <span className="edit-indicator">Edit Mode</span>
          </>
        ) : (
          'Add New Exercise'
        )}
      </h3>

      {/* Title (Search Suggestions) */}
      <div className="form-group">
        <label>Exercise Name*</label>
        <input
          type="text"
          placeholder="e.g. Bench Press"
          onChange={(e) => {
            setTitle(e.target.value);
            handleSearch(e.target.value);
          }}
          value={title}
          className={emptyFields.includes('title') ? 'error' : ''}
          autoComplete="off"
        />

        {/* Suggestion Dropdown */}
        {suggestions.length > 0 && (
          <div className="suggestions-box">
            {suggestions.map((exercise, index) => (
              <div
                key={index}
                className="suggestion-item"
                onClick={() => {
                  setTitle(exercise.name);
                  // Guess category based on wger bodyPart
                  const bp = (exercise.bodyPart || '').toLowerCase();
                  if (bp === 'cardiovascular' || bp === 'cardio') {
                    setCategory('Cardio');
                  } else if (bp === 'stretching' || bp === 'flexibility') {
                    setCategory('Flexibility');
                  } else {
                    setCategory('Strength');
                  }
                  setSuggestions([]);
                }}
              >
                <span className="suggestion-name">{exercise.name}</span>
                <span className="suggestion-tag">{exercise.bodyPart}</span>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Category Selection */}
      <div className="form-group">
        <label>Category</label>
        <select value={category} onChange={(e) => setCategory(e.target.value)}>
          <option value="Strength">Strength / Weightlifting</option>
          <option value="Cardio">Cardio / Cardiovascular</option>
          <option value="Flexibility">Flexibility / Stretching</option>
          <option value="Endurance">Endurance / Stamina</option>
        </select>
      </div>

      {/* Load & Reps */}
      <div className="form-row">
        <div className="form-group">
          <label>Load / Weight*</label>
          <div style={{ display: 'flex', gap: '4px' }}>
            <input
              type="number"
              placeholder="0"
              onChange={(e) => setLoad(e.target.value)}
              value={load}
              className={emptyFields.includes('load') ? 'error' : ''}
            />
            <select
              value={weightUnit}
              onChange={(e) => setWeightUnit(e.target.value)}
              style={{ width: '70px', padding: '6px' }}
            >
              <option value="lbs">lbs</option>
              <option value="kg">kg</option>
            </select>
          </div>
        </div>

        <div className="form-group">
          <label>Reps Completed*</label>
          <input
            type="number"
            placeholder="0"
            onChange={(e) => setReps(e.target.value)}
            value={reps}
            className={emptyFields.includes('reps') ? 'error' : ''}
          />
        </div>
      </div>

      {/* Date Picker */}
      <div className="form-group">
        <label>Workout Date</label>
        <input
          type="date"
          value={date}
          onChange={(e) => setDate(e.target.value)}
        />
      </div>

      {/* Notes Textarea */}
      <div className="form-group">
        <label>Notes / Feedback</label>
        <textarea
          placeholder="e.g. Last set felt heavy, check form next time"
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          maxLength={150}
        />
      </div>

      {/* Buttons */}
      <div className="form-buttons">
        <button type="submit" disabled={isSubmitting}>
          {isSubmitting ? (
            'Saving...'
          ) : editWorkout ? (
            <>
              <svg viewBox="0 0 24 24" width="16" height="16" fill="currentColor">
                <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z"/>
              </svg>
              Save Changes
            </>
          ) : (
            <>
              <svg viewBox="0 0 24 24" width="16" height="16" fill="currentColor">
                <path d="M19 13h-6v6h-2v-6H5v-2h6V5h2v6h6v2z"/>
              </svg>
              Add Workout
            </>
          )}
        </button>

        {editWorkout && (
          <button
            type="button"
            className="cancel-btn"
            onClick={onCancelEdit}
            disabled={isSubmitting}
          >
            Cancel Edit
          </button>
        )}
      </div>

      {error && <div className="error">{error}</div>}
    </form>
  );
};

export default WorkoutForm;